import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { BrandAnalyticsPanel } from "@/components/integrations/brand-analytics-panel";
import { DashboardPageHeader, DashboardPageShell } from "@/components/patterns/headers";
import { Skeleton } from "@/components/ui/skeleton";
import {
  hasEnabledBrandAnalytics,
  useFeatureFlags,
} from "@/hooks/use-feature-flags";
import { useSession } from "@/hooks/use-session";
import { useBrandStore } from "@/state/store/brand";

const analyticsSearchSchema = z.object({
  integration: z.enum(["success", "error"]).optional(),
  provider: z.enum(["google", "microsoft"]).optional(),
  reason: z.string().optional(),
  connectionId: z.string().optional(),
});

export const Route = createFileRoute("/_dashboard/$brand/analytics")({
  component: BrandAnalyticsPage,
  validateSearch: analyticsSearchSchema,
});

function BrandAnalyticsPage() {
  const navigate = useNavigate({ from: Route.fullPath });
  const { brand } = Route.useParams();
  const search = Route.useSearch();
  const { sessionData } = useSession();
  const { flags, isLoading } = useFeatureFlags();
  const selectedBrandId = useBrandStore((state) => state.selectedBrandId);
  const canManageIntegrations =
    sessionData?.app_role === "ADMIN" || sessionData?.app_role === "OWNER";
  const showBrandAnalytics = hasEnabledBrandAnalytics(flags.analytics);

  useEffect(() => {
    if (!isLoading && !showBrandAnalytics) {
      navigate({ to: "/$brand/dashboard", params: { brand }, replace: true });
    }
  }, [brand, isLoading, navigate, showBrandAnalytics]);

  useEffect(() => {
    if (!search.integration) {
      return;
    }

    if (search.integration === "success") {
      toast.success(
        search.provider === "google"
          ? "Google connected. We are refreshing the available analytics resources."
          : "Provider connected successfully.",
      );
    } else {
      const providerLabel = search.provider === "google" ? "Google" : search.provider === "microsoft" ? "Microsoft" : "The provider";
      toast.error(search.reason ? `${providerLabel} setup failed: ${search.reason}` : `${providerLabel} setup failed.`);
    }

    navigate({
      to: "/$brand/analytics",
      params: { brand },
      search: {},
      replace: true,
    });
  }, [brand, navigate, search.integration, search.provider, search.reason]);

  if (isLoading) {
    return (
      <DashboardPageShell as="div" contentClassName="space-y-6">
        <DashboardPageHeader
          title="Analytics"
          description="Track how AI assistants drive traffic to your brand."
        />
        <Skeleton className="h-64 w-full rounded-xl" />
      </DashboardPageShell>
    );
  }

  if (!showBrandAnalytics) {
    return null;
  }

  return (
    <DashboardPageShell as="div" contentClassName="space-y-6">
      <DashboardPageHeader
        title="Analytics"
        description="Track how AI assistants drive traffic to your brand."
      />
      <BrandAnalyticsPanel
        brandId={selectedBrandId}
        canManage={canManageIntegrations}
        providerAvailability={flags.analytics}
      />
    </DashboardPageShell>
  );
}
